import * as React from 'react';
import { FC, memo, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import withAddIncentiveToCart from 'components/with-add-incentive-to-cart';
import { RedemptionMethod } from 'components/with-add-incentive-to-cart/types';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { removeAppliedRewardsInStorage } from 'state/global-state/models/loyalty/rewards/rewards.utils';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { isLoyaltyOffer, isReward } from 'state/loyalty/types';
import { useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';

import { parseIncentiveData } from '../incentive-card/parse-incentive-data';
import { InvalidServiceModeModal } from '../invalid-service-mode-modal';
import { useLoyaltyIncentivesAvailability } from '../use-loyalty-incentives-availability';

import { IncentiveDetailsView } from './incentive-details.view';
import { IIncentiveDetailsProps } from './types';

/**
 *
 * IncentiveDetails displays an expanded view of a incentive when selected from the incentive list
 *
 */
const IncentiveDetails: FC<IIncentiveDetailsProps> = ({
  selectedIncentive,
  engineIncentivesMap,
  addIncentiveToCart,
}) => {
  const incentiveLoyaltyEngineId = selectedIncentive?.loyaltyEngineId || '';
  const engineReward = engineIncentivesMap[incentiveLoyaltyEngineId];

  const {
    pointCost,
    locked,
    redemptionPercentage,
    remainingPointsNeededForRedemption,
    rewardBenefitId,
  } = engineReward || {};

  const { removeFromCart } = useOrderContext();
  const {
    inRestaurantLoyaltyEnabledAtRestaurant,
    inRestaurantRedemptionEnabled,
    clearInRestaurantRedemptionAllRewards,
  } = useInRestaurantRedemptionContext();
  const rewardData = parseIncentiveData(selectedIncentive);
  const { noStoreSelected } = useStoreContext();
  const { formatMessage } = useIntl();
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [showRemoveRewardButton, setShowRemoveRewardButton] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const enableMobileOffer = useFlag(LaunchDarklyFlag.ENABLE_OFFERS_MOBILE);
  const { _id: incentiveId, _type: incentiveType } =
    useMemo(() => {
      return isReward(selectedIncentive)
        ? selectedIncentive.incentives?.find(incentive => incentive?._id === rewardBenefitId)
        : selectedIncentive;
    }, [rewardBenefitId, selectedIncentive]) || {};
  const dispatch = useAppDispatch();
  const appliedLoyaltyRewardsArray = useAppSelector(
    selectors.loyalty.selectAppliedLoyaltyRewardsArray
  );
  const offersFeedbackMap = useAppSelector(selectors.loyalty.selectOfferFeedbackMap);
  const loyaltyOffersInCooldown = useAppSelector(selectors.loyalty.selectLoyaltyOffersInCooldown);

  const {
    checkIncentiveAvailability,
    incentiveUnavailableMessage,
  } = useLoyaltyIncentivesAvailability(selectedIncentive, pointCost, locked);
  const exceedsBalanceError = formatMessage({ id: 'exceedsRewardsBalance' });

  useEffect(() => {
    if (incentiveUnavailableMessage) {
      setErrorMessage(incentiveUnavailableMessage);
      setShowRemoveRewardButton(incentiveUnavailableMessage === exceedsBalanceError);
      return;
    }
    if (noStoreSelected) {
      setErrorMessage('');
      return;
    }

    // Check incentive item availability in selected store
    (async () => {
      setErrorMessage('');
      setCheckingAvailability(true);
      try {
        const isAvailable = await checkIncentiveAvailability(`${incentiveType}-${incentiveId}`);
        const storeError = isAvailable ? '' : formatMessage({ id: 'nonAvailableItemInStore' });
        setErrorMessage(storeError);
        setCheckingAvailability(false);
      } catch {
        setCheckingAvailability(false);
      }
    })();
  }, [
    incentiveId,
    incentiveType,
    checkIncentiveAvailability,
    exceedsBalanceError,
    formatMessage,
    noStoreSelected,
    incentiveUnavailableMessage,
  ]);

  const incentiveEvaluationResults = offersFeedbackMap[incentiveLoyaltyEngineId];
  const handleRemoveRewardsClick = () => {
    if (appliedLoyaltyRewardsArray?.length) {
      appliedLoyaltyRewardsArray.forEach(appliedReward => {
        removeFromCart(appliedReward);
        dispatch(
          actions.loyalty.removeAppliedReward({
            cartId: appliedReward.cartId,
            rewardBenefitId: appliedReward.rewardId,
          })
        );
      });
      clearInRestaurantRedemptionAllRewards();
      removeAppliedRewardsInStorage();

      setShowRemoveRewardButton(false);
    }
  };

  const canAddToInRestaurantOrder =
    (isLoyaltyOffer(selectedIncentive) && !selectedIncentive.mobileOrderOnly) ||
    (isReward(selectedIncentive) &&
      inRestaurantLoyaltyEnabledAtRestaurant &&
      inRestaurantRedemptionEnabled);

  const canAddToMobileOrder =
    (isLoyaltyOffer(selectedIncentive) && enableMobileOffer) || isReward(selectedIncentive);

  return (
    <>
      {!checkingAvailability && incentiveEvaluationResults?.length && (
        <InvalidServiceModeModal incentiveEvaluationResults={incentiveEvaluationResults} />
      )}
      <IncentiveDetailsView
        {...rewardData}
        pointCost={pointCost}
        locked={locked}
        redemptionPercentage={redemptionPercentage}
        handleRedeemRewardClick={() =>
          addIncentiveToCart({ incentive: selectedIncentive, method: RedemptionMethod.MOBILE })
        }
        handleInRestaurantRedemptionClick={() =>
          addIncentiveToCart({
            incentive: selectedIncentive,
            method: RedemptionMethod.IN_RESTAURANT,
          })
        }
        remainingPointsNeededForRedemption={remainingPointsNeededForRedemption}
        errorMessage={errorMessage}
        key={rewardData.id}
        checkingAvailability={checkingAvailability}
        disableRedemption={!!errorMessage}
        canAddToInRestaurantOrder={canAddToInRestaurantOrder}
        canAddToMobileOrder={canAddToMobileOrder}
        showRemoveRewardButton={showRemoveRewardButton}
        handleRemoveRewardsClick={handleRemoveRewardsClick}
        incentiveEvaluationResults={incentiveEvaluationResults}
        offersInCooldown={loyaltyOffersInCooldown}
      />
    </>
  );
};

export default memo(withAddIncentiveToCart(IncentiveDetails));
